import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

import { VAT_CONDITION_LABELS, can, companyCreateSchema } from "@devengo/shared";

import {
  Button,
  Card,
  ErrorBox,
  Field,
  Input,
  PageTitle,
  Select,
  tableClass,
  tdClass,
  thClass,
} from "../components/ui";
import { matches } from "../components/search-select";
import { api } from "../lib/api";
import { MONTHS, formatCuit } from "../lib/format";
import { useCompanies, type Company } from "../lib/hooks";
import { useCurrentUser } from "../lib/session";

type CompanyForm = z.input<typeof companyCreateSchema>;

export function CompaniesPage() {
  const user = useCurrentUser();
  const companies = useCompanies();
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");
  const canCreate = can(user.role, "createCompanies");
  const shown = (companies.data ?? []).filter((c) =>
    matches({ id: c.id, label: c.legalName, detail: c.cuit }, query),
  );

  return (
    <>
      <PageTitle
        subtitle="Las empresas clientes del estudio. Elegí una para trabajar su ciclo de IVA."
        actions={
          <>
            <div className="w-80">
              <Input
                placeholder="Buscar por razón social o CUIT"
                aria-label="Buscar empresa"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            {canCreate && !creating ? (
              <Button onClick={() => setCreating(true)}>Nueva empresa</Button>
            ) : null}
          </>
        }
      >
        Empresas
      </PageTitle>
      {creating ? <NewCompany onDone={() => setCreating(false)} /> : null}
      <Card>
        {companies.data?.length === 0 ? (
          <p className="text-sm text-brand-muted">
            Todavía no hay empresas.{" "}
            {canCreate
              ? "Cargá la primera con «Nueva empresa»."
              : "Pedile a un contador que la cargue."}
          </p>
        ) : (
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={thClass}>Razón social</th>
                <th className={thClass}>CUIT</th>
                <th className={thClass}>Condición IVA</th>
                <th className={thClass}>Cierre de ejercicio</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((c: Company) => (
                <tr key={c.id} className="hover:bg-brand-tint">
                  <td className={tdClass}>
                    <Link
                      to="/empresas/$companyId/comprobantes"
                      params={{ companyId: c.id }}
                      className="font-medium text-brand-primary hover:underline"
                    >
                      {c.legalName}
                    </Link>
                  </td>
                  <td className={`${tdClass} tabular`}>{formatCuit(c.cuit)}</td>
                  <td className={tdClass}>
                    {VAT_CONDITION_LABELS[c.vatCondition as keyof typeof VAT_CONDITION_LABELS] ??
                      c.vatCondition}
                  </td>
                  <td className={tdClass}>{MONTHS[c.fiscalYearClosingMonth - 1]}</td>
                </tr>
              ))}
              {shown.length === 0 ? (
                <tr>
                  <td className={`${tdClass} text-brand-muted`} colSpan={4}>
                    Ninguna empresa coincide con «{query}».
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        )}
        <ErrorBox error={companies.error} />
      </Card>
    </>
  );
}

function NewCompany({ onDone }: { onDone: () => void }) {
  const client = useQueryClient();
  const form = useForm<CompanyForm>({
    resolver: zodResolver(companyCreateSchema),
    defaultValues: { fiscalYearClosingMonth: 12, vatCondition: "responsable_inscripto" },
  });
  const create = useMutation({
    mutationFn: (input: CompanyForm) => api.post<Company>("/api/companies", input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["companies"] });
      onDone();
    },
  });
  const errors = form.formState.errors;
  return (
    <Card title="Nueva empresa" className="mb-4">
      <form
        className="grid grid-cols-1 gap-3 md:grid-cols-4"
        onSubmit={form.handleSubmit((values) => create.mutate(values))}
      >
        <Field label="CUIT" error={errors.cuit?.message}>
          <Input placeholder="30-12345678-9" {...form.register("cuit")} />
        </Field>
        <Field label="Razón social" error={errors.legalName?.message}>
          <Input {...form.register("legalName")} />
        </Field>
        <Field label="Condición frente al IVA" error={errors.vatCondition?.message}>
          <Select {...form.register("vatCondition")}>
            <option value="responsable_inscripto">Responsable inscripto</option>
            <option value="monotributo">Monotributo</option>
            <option value="exento">Exento</option>
          </Select>
        </Field>
        <Field label="Mes de cierre de ejercicio">
          <Select {...form.register("fiscalYearClosingMonth", { valueAsNumber: true })}>
            {MONTHS.map((name, i) => (
              <option key={name} value={i + 1}>
                {name}
              </option>
            ))}
          </Select>
        </Field>
        <div className="flex items-center gap-2 md:col-span-4">
          <Button type="submit" disabled={create.isPending}>
            Guardar empresa
          </Button>
          <Button variant="secondary" onClick={onDone}>
            Cancelar
          </Button>
          <span className="text-xs text-brand-muted">
            Los períodos se abren solos con el primer comprobante de cada mes.
          </span>
        </div>
        <div className="md:col-span-4">
          <ErrorBox error={create.error} />
        </div>
      </form>
    </Card>
  );
}
